"use client"

import React, { useState, useEffect, useRef } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { onAuthStateChanged, signOut } from "firebase/auth"
import { auth, db } from "../../firebase"
import { collection, getDocs, query, where, doc, getDoc, limit } from "firebase/firestore"
import { Search, Menu, X, User, LogOut, PlusCircle, List, Clock, TrendingUp, ChevronUp, ChevronDown } from "lucide-react"
import "./Navbar.css"
import NotificationSystem from "../Notifications/NotificationSystem"

const Navbar = () => {
  const [user, setUser] = useState(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const searchRef = useRef(null)
  const searchResultsRef = useRef(null)
  const searchTimeout = useRef(null)
  const profileDropdownRef = useRef(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [recentSearches, setRecentSearches] = useState([])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser)
      
      if (currentUser) {
        // Force token refresh on mobile to prevent login issues
        currentUser.getIdToken(true).catch(err => {
          console.error("Error refreshing token:", err)
        })
        
        // Check admin status for navigation
        const fetchUserData = async () => {
          try {
            const userDocRef = doc(db, 'users', currentUser.uid)
            const userSnap = await getDoc(userDocRef)
            
            if (userSnap.exists() && userSnap.data().isAdmin) {
              setIsAdmin(true)
            }
          } catch (error) {
            console.error("Error fetching user data:", error)
          }
        }
        
        fetchUserData()
      } else {
        setIsAdmin(false)
      }
    })
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    // Close mobile menu when navigating
    setMobileMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    // Close search dropdown on click outside
    function handleClickOutside(event) {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowSearchResults(false)
      }

      // Close profile dropdown when clicking outside
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    // Load recent searches from localStorage on component mount
    const savedSearches = localStorage.getItem('recentSearches')
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches).slice(0, 5))
      } catch (e) {
        console.error("Failed to parse recent searches:", e)
        setRecentSearches([])
      }
    }
  }, [])

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  }

  const handleSearch = async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    if (searchQuery.trim()) {
      saveRecentSearch(searchQuery.trim());
      
      if (e) {
        // Form submission case
        navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setShowSearchResults(false);
        setSearchResults([]);
      } else {
        // Async search case
        setSearchLoading(true);
        setShowSearchResults(true);
        
        try {
          const queriesRef = collection(db, "queries");
          const q = query(
            queriesRef,
            where("title", ">=", searchQuery.toLowerCase()),
            where("title", "<=", searchQuery.toLowerCase() + '\uf8ff'),
            limit(5)
          );
          const querySnapshot = await getDocs(q);
          
          const fullTextQuery = query(
            queriesRef,
            limit(10)
          );
          const fullTextSnapshot = await getDocs(fullTextQuery);
          
          const allResults = [
            ...querySnapshot.docs.map(doc => ({
              id: doc.id,
              title: doc.data().title,
              tags: doc.data().tags || [],
              description: doc.data().description
            })),
            ...fullTextSnapshot.docs
              .filter(doc => 
                !querySnapshot.docs.some(d => d.id === doc.id) &&
                (doc.data().title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                doc.data().description.toLowerCase().includes(searchQuery.toLowerCase()))
              )
              .map(doc => ({
                id: doc.id,
                title: doc.data().title,
                tags: doc.data().tags || [],
                description: doc.data().description
              }))
          ];
          
          const results = allResults
            .sort((a, b) => {
              const aInTitle = a.title.toLowerCase().includes(searchQuery.toLowerCase());
              const bInTitle = b.title.toLowerCase().includes(searchQuery.toLowerCase());
              
              if (aInTitle && !bInTitle) return -1;
              if (!aInTitle && bInTitle) return 1;
              return 0;
            })
            .slice(0, 5)
            .map(doc => ({
              ...doc,
              highlightedTitle: highlightMatch(doc.title, searchQuery),
              highlightedDesc: truncateAndHighlight(doc.description, searchQuery, 80)
            }));
            
          setSearchResults(results);
        } catch (error) {
          console.error("Search error:", error);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (value.trim().length > 1) {
      setSearchLoading(true);
      setShowSearchResults(true);
      
      searchTimeout.current = setTimeout(() => {
        handleSearch();
      }, 300);
    } else {
      setShowSearchResults(value.trim().length > 0);
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  const handleSearchResultClick = (id) => {
    navigate(`/query/${id}`)
    setShowSearchResults(false)
    setSearchQuery("")
  }


  const saveRecentSearch = (query) => {
    const updatedSearches = [
      query,
      ...recentSearches.filter(search => search !== query)
    ].slice(0, 5);
    setRecentSearches(updatedSearches);
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  };

  const handleSearchSelect = (query) => {
    saveRecentSearch(query)
    navigate(`/search?q=${encodeURIComponent(query)}`)
    setSearchQuery(query)
    setShowSearchResults(false)
    setSearchResults([])
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth)
      navigate("/")
    } catch (error) {
      console.error("Error signing out:", error)
    }
  }

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(regex, "<mark>$1</mark>");
  };
  
  // Truncate text and highlight matches
  const truncateAndHighlight = (text, query, maxLength) => {
    if (!text) return '';
    if (!query) return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const match = text.match(regex);
    
    if (!match) {
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    }
    
    const matchIndex = text.toLowerCase().indexOf(query.toLowerCase());
    const start = Math.max(0, matchIndex - 20);
    const end = Math.min(text.length, matchIndex + query.length + 20);
    
    let result = text.substring(start, end);
    if (start > 0) result = '...' + result;
    if (end < text.length) result = result + '...';
    
    return result.replace(regex, '<mark>$1</mark>');
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-left">
          <Link to="/" className="navbar-logo court-brand-logo">
            <span className="brand-gavel">⚖️</span>
            <div className="brand-text-block">
              <span className="brand-main">The Kangaroo Court</span>
              <span className="brand-sub">Vault of Shame</span>
            </div>
          </Link>
        </div>

        <div className="navbar-center">
          <div className="navbar-search" ref={searchRef}>
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-container">
                <Search className="search-icon" size={16} />
                <input
                  type="text"
                  placeholder="Search the Docket & Vault..."
                  value={searchQuery}
                  onChange={handleInputChange}
                  onFocus={() => setShowSearchResults(true)}
                  className="search-input"
                />
                {searchQuery && (
                  <button 
                    type="button" 
                    className="clear-search-btn"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button type="submit" className="search-button">
                Search
              </button>
            </form>

            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {showSearchResults && (
              <div className="search-results-dropdown" ref={searchResultsRef}>
                {searchLoading ? (
                  <div className="search-loading">Searching dockets...</div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="search-results-section">
                      <div className="search-results-header">
                        <TrendingUp size={14} />
                        <span>Matching Cases</span>
                      </div>
                      {searchResults.map((result) => (
                        <div
                          key={result.id}
                          className="search-result-item"
                          onClick={() => handleSearchResultClick(result.id)}
                        >
                          <div 
                            className="search-result-title" 
                            dangerouslySetInnerHTML={{ __html: result.highlightedTitle }}
                          ></div>
                          <div 
                            className="search-result-desc" 
                            dangerouslySetInnerHTML={{ __html: result.highlightedDesc }}
                          ></div>
                          {result.tags.length > 0 && (
                            <div className="search-result-tags">
                              {result.tags.slice(0, 2).map((tag, idx) => (
                                <span key={idx} className="search-result-tag">{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="search-footer">
                      <button 
                        className="view-all-results" 
                        onClick={() => handleSearch({ preventDefault: () => {} })}
                      >
                        View all dockets
                      </button>
                    </div>
                  </>
                ) : searchQuery.trim().length > 0 ? (
                  <div className="no-results">No cases found</div>
                ) : recentSearches.length > 0 ? (
                  <>
                    <div className="search-results-section">
                      <div className="search-results-header">
                        <Clock size={14} />
                        <span>Recent Searches</span>
                        <button 
                          className="clear-recents-btn" 
                          onClick={() => {
                            setRecentSearches([])
                            localStorage.removeItem('recentSearches')
                          }}
                        >
                          Clear
                        </button>
                      </div>
                      {recentSearches.map((term, index) => (
                        <div
                          key={index}
                          className="search-result-item recent"
                          onClick={() => handleSearchSelect(term)}
                        >
                          <Clock size={14} className="recent-search-icon" />
                          <span>{term}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="navbar-right">
          <ul className={`navbar-menu ${mobileMenuOpen ? "mobile-open" : ""}`}>
            <li className="navbar-menu-item">
              <Link to="/browse" className="navbar-link">
                <List size={18} className="navbar-icon" />
                <span>The Docket</span>
              </Link>
            </li>
            <li className="navbar-menu-item">
              <Link to="/post" className="navbar-link">
                <PlusCircle size={18} className="navbar-icon" />
                <span>File a Case</span>
              </Link>
            </li>
            <li className="navbar-menu-item">
              <Link to="/rewards" className="navbar-link">
                <span className="navbar-icon">⚖️</span>
                <span>Bail Store</span>
              </Link>
            </li>
            
            {isAdmin && (
              <li className="navbar-menu-item">
                <Link to="/admin" className="navbar-link admin-link">
                  <span>Chief Justice</span>
                </Link>
              </li>
            )}

            {user && (
              <li className="navbar-menu-item notification-menu-item">
                <NotificationSystem />
              </li>
            )}

            {user ? (
              <li className="navbar-menu-item profile-menu-item" ref={profileDropdownRef}>
                <div className="navbar-profile">
                  <button onClick={toggleDropdown} className="navbar-profile-button">
                    <img 
                      src={user.photoURL || "/placeholder.svg"} 
                      alt="Profile" 
                      className="navbar-profile-img" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder.svg";
                      }}
                    />
                    <span className="navbar-username">
                      {user.displayName || user.email}
                    </span>
                    {dropdownOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
                {dropdownOpen && (
                  <div className="dropdown-menu">
                    <ul>
                      <li className="dropdown-item">
                        <Link 
                          to="/dashboard" 
                          className="dropdown-link" 
                          onClick={() => setDropdownOpen(false)}
                        >
                          <User size={18} className="dropdown-icon" />
                          Profile
                        </Link>
                      </li>
                      <li className="dropdown-item">
                        <button onClick={handleSignOut} className="dropdown-link">
                          <LogOut size={18} className="dropdown-icon" />
                          Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </li>
            ) : (
              <li className="navbar-menu-item">
                <Link to="/login" className="login-button">
                  Login
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
